import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Nav, NavDropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { AnyType } from '../interfaces';
import { AppRootState } from '../redux';
import useFieldApi, { UseFieldApiConfig } from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import { ScrollToElement, renderTenancyLinksAsNavItems, getConfigurationDataKey } from '../utils';
import { CustomDurgSpecificNavDropdownLinks } from './CustomDurgSpecificNavDropdownLinks';
import { FeatureGuard } from './multi-tenant/FeatureGuard';

export const AppNavigation = () => {
  const dispatch = useDispatch();
  const primaryMenu = useSelector(
    (state: AppRootState) =>
      (((state.app.entities?.menus as AnyType) ?? {})[getConfigurationDataKey('primary')] as AnyType)?.data
  );

  const currentPath = window.location.pathname;
  const [activeItem, setActiveItem] = useState<string | null>(null);

  useEffect(() => {
    setActiveItem(currentPath);
  }, [currentPath]); // Set active menu item on page load

  const handleSetActive = (url: string) => {
    setActiveItem(url);
  };

  return (
    <>
      {primaryMenu?.links
        ?.filter((link?: AnyType) => !link?.type)
        ?.map((parentLink: AnyType) => {
          if (!parentLink.links || parentLink.links?.length === 0) {
            const kebabLabel = _.kebabCase(parentLink.label);
            const isActive = activeItem === parentLink.url;
            return (
              <FeatureGuard key={kebabLabel} contentKey={parentLink.featureKey}>
                <div
                  className={`d-flex align-items-center nav-dropdown ${isActive ? 'active' : ''}`}
                  onClick={() => handleSetActive(parentLink.url)}>
                  {parentLink.label === 'Requests' ? (
                    <span className='ps-2'>
                      <i className={`fas fa-light fa-cabinet-filing`}></i>
                    </span>
                  ) : parentLink.label === 'Patients' ? (
                    <span className='ps-2'>
                      <i className={`fas fa-light fa-user-injured`}></i>
                    </span>
                  ) : parentLink.label === 'Messages' ? (
                    <span className='ps-2'>
                      <i className={`fas fa-light fa-envelope`}></i>
                    </span>
                  ) : parentLink.label === 'Dashboard' ? (
                    <span className='ps-2'>
                      <i className={`fas fa-light fa-dashboard`}></i>
                    </span>
                  ) : parentLink.label === 'Resources' ? (
                    <span className='ps-2'>
                      <i className={`fas fa-light fa-books-medical`}></i>
                    </span>
                  ) : (
                    <></>
                  )}

                  <Nav.Link
                    className={`mx-2 nav-dropdown menu-navigation ${isActive ? 'active' : ''}`}
                    href={parentLink.url}
                    data-testid={`cmx__${kebabLabel}`}
                    onClick={() => handleSetActive(parentLink.url)}>
                    {parentLink.label}
                  </Nav.Link>
                </div>
              </FeatureGuard>
            );
          }

          const kebabLabel = _.kebabCase(`${parentLink.label}-dropdown`);
          const isDropdownActive = parentLink.links?.some((link: AnyType) => activeItem === link.url);

          return (
            <FeatureGuard key={kebabLabel} contentKey={parentLink.featureKey}>
              <div className='d-flex align-items-center nav-dropdown'>
                <NavDropdown
                  className={`mx-2 ${isDropdownActive ? 'active' : ''}`}
                  id={kebabLabel}
                  title={parentLink.label}
                  data-testid={`cmx__${kebabLabel}`}
                  onClick={() => handleSetActive(parentLink.url)}>
                  {renderTenancyLinksAsNavItems(dispatch, parentLink.links)}
                </NavDropdown>
              </div>
            </FeatureGuard>
          );
        })}
      {primaryMenu?.links
        ?.filter((link?: AnyType) => 'drug-specific-dropdown' === link?.type)
        ?.map((parentLink: AnyType, index: number) => (
          <div className='nav-dropdown d-flex align-items-center' key={index}>
            {parentLink.label === 'Important Safety Information' ? (
              <span className='ps-2'>
                <i className={`fas fa-light fa-life-ring`}></i>
              </span>
            ) : parentLink.label === 'Prescribing Information' ? (
              <span className='ps-2'>
                <i className={`fas fa-light fa-prescription-bottle-medical`}></i>
              </span>
            ) : (
              <></>
            )}
            <CustomDurgSpecificNavDropdownLinks
              key={_.kebabCase(`${parentLink.label}-dropdown`)}
              parentEle={parentLink}
            />
          </div>
        ))}
      {primaryMenu?.links
        ?.filter((link?: AnyType) => 'scrollToElement' === link?.type)
        ?.map((link: AnyType) => (
          <ScrollToElement key={_.kebabCase(`${link.label}`)} parentEle={link} />
        ))}
    </>
  );
};
